import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import QRScannerDialog from './QRScannerDialog';
import NotificationBell from './NotificationBell';
import {
  Home,
  ClipboardList,
  Box,
  History,
  Users,
  Settings,
  LogOut,
  CheckCircle,
  BarChart3,
  FileText,
  QrCode,
  Award,
  AlertTriangle,
  Menu,
  X,
  UserCircle,
  Lock,
  Warehouse,
  ScrollText,
  IndianRupee,
  Package,
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_86ba7c87-2c5f-4809-b4ca-291f6e117aa2/artifacts/wjmihcrt_black%20rento%20logo.png";

const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const checkAccess = (menuId) => {
    if (isAdmin) return true;
    if (user?.permissions && user.permissions[menuId] === true) return true;
    return false;
  };

  const navItems = [
    { id: 'dashboard', path: '/dashboard', label: 'Dashboard', icon: Home, show: true },
    { id: 'new_qc', path: '/qc/new', label: 'New QC', icon: ClipboardList, show: true },
    { id: 'my_qcs', path: '/qc/list', label: 'My QCs', icon: FileText, show: true },
    { id: 'second_check', path: '/second-check', label: 'Second Check', icon: CheckCircle, show: true },
    { id: 'inventory', path: '/inventory', label: 'Inventory', icon: Warehouse, show: checkAccess('inventory') },
    { id: 'assets', path: '/assets', label: 'Assets', icon: Box, show: checkAccess('assets') },
    { id: 'contracts', path: '/contracts', label: 'Contracts', icon: ScrollText, show: false },
    { id: 'billing', path: '/billing', label: 'Billing', icon: IndianRupee, show: false },
    { id: 'customers', path: '/customers', label: 'Customers', icon: UserCircle, show: checkAccess('customers') },
    { id: 'history', path: '/history', label: 'History', icon: History, show: checkAccess('history') },
    { id: 'reports', path: '/reports', label: 'Reports', icon: BarChart3, show: checkAccess('reports') },
    { id: 'performance', path: '/technician-scoring', label: 'Performance', icon: Award, show: checkAccess('performance') },
    { id: 'alerts', path: '/predictive-alerts', label: 'Alerts', icon: AlertTriangle, show: checkAccess('alerts') },
    { id: 'users', path: '/users', label: 'Users', icon: Users, show: checkAccess('users') },
    { id: 'settings', path: '/settings', label: 'Settings', icon: Settings, show: checkAccess('settings') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className={`hidden md:fixed md:left-0 md:top-0 md:bottom-0 md:flex md:flex-col bg-[#1a1a1a] text-white transition-all duration-300 ${sidebarCollapsed ? 'md:w-16' : 'md:w-64'}`}>
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          {!sidebarCollapsed && (
            <Link to="/dashboard" className="block">
              <img src={LOGO_URL} alt="rentOcart.com" className="h-12 object-contain" />
            </Link>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors ml-auto"
          >
            {sidebarCollapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
          </button>
        </div>
        {!sidebarCollapsed && (
          <p className="text-xs text-gray-500 px-4 pb-2">Quality Control System</p>
        )}

        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {navItems
            .filter((item) => item.show)
            .map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  title={sidebarCollapsed ? item.label : ''}
                  className={`flex items-center px-3 py-3 rounded-lg transition-colors ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} ${
                    isActive(item.path)
                      ? 'bg-gradient-to-r from-cyan-500/20 to-green-500/20 text-cyan-400 border-l-2 border-cyan-400'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!sidebarCollapsed && <span className="font-medium">{item.label}</span>}
                </Link>
              );
            })}
        </nav>

        {!sidebarCollapsed && (
          <div className="p-4 border-t border-gray-800">
            <div className="flex items-center space-x-3 px-4 py-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-green-500 flex items-center justify-center font-bold text-white">
                {user?.full_name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate text-white">{user?.full_name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.role}</p>
              </div>
              <NotificationBell />
            </div>
            <Link to="/change-password">
              <Button variant="ghost" className="w-full justify-start text-gray-400 hover:bg-gray-800 hover:text-white mb-2">
                <Lock className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            </Link>
            <Button
              onClick={handleLogout}
              data-testid="logout-button"
              variant="outline"
              className="w-full bg-transparent border-gray-700 text-white hover:bg-gray-800 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        )}

        {sidebarCollapsed && (
          <div className="p-2 border-t border-gray-800 flex flex-col items-center space-y-2">
            <NotificationBell />
            <button onClick={handleLogout} className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors" title="Logout">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'} min-h-screen flex flex-col`}>
        {/* Mobile Header */}
        <header className="md:hidden sticky top-0 z-30 bg-[#1a1a1a] border-b border-gray-800 px-4 py-2 flex items-center justify-between shadow-sm">
          <Link to="/dashboard">
            <img src={LOGO_URL} alt="rentOcart.com" className="h-10 object-contain" />
          </Link>
          <div className="flex items-center space-x-2">
            <NotificationBell />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowQRScanner(true)}
              data-testid="mobile-qr-button"
              className="border-gray-600 text-white hover:bg-gray-800"
            >
              <QrCode className="w-5 h-5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              data-testid="mobile-menu-button"
              className="border-gray-600 text-white hover:bg-gray-800"
            >
              {showMobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </header>

        {/* Mobile Menu Dropdown */}
        {showMobileMenu && (
          <div className="md:hidden fixed top-[57px] left-0 right-0 z-40 bg-[#1a1a1a] border-b border-gray-800 shadow-lg animate-in slide-in-from-top-2">
            <div className="p-4 border-b border-gray-800 bg-gray-900/50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-green-500 flex items-center justify-center font-bold text-white">
                  {user?.full_name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate text-white">{user?.full_name}</p>
                  <p className="text-xs text-gray-400 truncate">{user?.role}</p>
                </div>
              </div>
            </div>
            <div className="p-2 max-h-[60vh] overflow-y-auto bg-[#1a1a1a]">
              {navItems
                .filter((item) => item.show)
                .map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setShowMobileMenu(false)}
                      data-testid={`mobile-menu-${item.label.toLowerCase().replace(' ', '-')}`}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive(item.path)
                          ? 'bg-gradient-to-r from-cyan-500/20 to-green-500/20 text-cyan-400'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  );
                })}
            </div>
            <div className="p-2 border-t border-gray-800">
              <Link
                to="/change-password"
                onClick={() => setShowMobileMenu(false)}
                className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors mb-2"
              >
                <Lock className="w-5 h-5" />
                <span className="font-medium">Change Password</span>
              </Link>
              <Button
                onClick={() => { setShowMobileMenu(false); handleLogout(); }}
                data-testid="mobile-logout-button"
                variant="destructive"
                className="w-full"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        )}

        {/* Desktop QR Button */}
        <div className="hidden md:block fixed bottom-6 right-6 z-40">
          <Button
            onClick={() => setShowQRScanner(true)}
            data-testid="desktop-qr-button"
            size="lg"
            className="h-14 w-14 rounded-full shadow-lg shadow-primary/30"
          >
            <QrCode className="w-6 h-6" />
          </Button>
        </div>

        {/* Content */}
        <main className="flex-1 p-4 md:p-8 pb-20 md:pb-8">{children}</main>

        <QRScannerDialog open={showQRScanner} onClose={() => setShowQRScanner(false)} />

        {/* Mobile Bottom Navigation */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-white border-t flex items-center justify-around z-40 shadow-lg">
          {navItems
            .filter((item) => item.show)
            .slice(0, 5)
            .map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                    isActive(item.path) ? 'text-primary' : 'text-muted-foreground'
                  }`}
                >
                  <Icon className="w-6 h-6 mb-1" />
                  <span className="text-xs font-medium">{item.label}</span>
                </Link>
              );
            })}
        </nav>
      </div>
    </div>
  );
};

export default Layout;